/**
 * The settings form of a module instance, drawn from the module's schema (ADR-025 §8): one
 * control per field kind (`FIELD_CONTROLS`), secrets write-only (keep / new value / reference),
 * required rules, the form's own checks, then a draft saved on the server, which checks again.
 */

import { Download, Save } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '../../api';
import type { IntegrationEnvironment } from '../../api/areas/integrations';
import { CORE_MODULE, type ModuleBranchId, type ModuleChoice, type ModuleField, type ModuleInstance, type ModuleManifest, type ModuleVersion, type SaveModuleDraftBody } from '../../api/areas/modules';
import { Callout, Field } from '../../components/blocks';
import { useWrite } from '../../components/Write';
import { formatCount } from '../../lib/format';
import { ConfirmCheck, emptySecret, EnvironmentBanner, ReasonField, secretDraftValid, SecretField, secretInput, SwitchRow, type SecretDraft } from '../integrations/shared';
import { checkValue, FIELD_CONTROLS, formatBytes, groupFields, initialValue, isBlank, isRequired, isTextFile, isWriteOnly, payloadValue, type FormErrorCode, type FormValue } from './fieldKinds';
import { ModuleError, ModuleWarnings, useText } from './ui';

/** Where a field's options come from when the manifest names an admin action (`options_from`). */
export interface OptionsSource {
  branch: ModuleBranchId;
  module: string;
  instanceKey: string;
  /** Admin actions run on the version in force: only then can the options be loaded. */
  active: boolean;
}

function storedSecret(base: ModuleVersion | null, key: string): boolean {
  const s = base?.secrets[key];
  return !!s?.configured && !s.destroyed;
}

/** The hint under a field: required or not, its default, its rule. */
function useFieldHint(): (field: ModuleField, required: boolean) => ReactNode {
  const { t } = useTranslation('modules');
  const text = useText();
  return (field, required) => {
    const k = field.kind;
    const rule =
      k.type === 'text'
        ? [t('form.maxLength', { count: k.max_len }), k.pattern ? t('form.pattern') : null]
        : k.type === 'integer'
          ? [t('form.range', { min: formatCount(k.min), max: formatCount(k.max) }), k.unit ? t(`unit.${k.unit}`) : null]
          : k.type === 'url'
            ? [k.https_only ? t('form.httpsOnly') : null]
            : k.type === 'time_of_day'
              ? [t('form.timeHint')]
              : k.type === 'phone'
                ? [t('form.phoneHint')]
                : k.type === 'path'
                  ? [t('form.pathHint')]
                  : k.type === 'email'
                    ? [t('form.emailHint')]
                    : [];
    const def = field.default !== null && k.type !== 'boolean' ? t('form.defaultValue', { value: Array.isArray(field.default) ? field.default.join(', ') : String(field.default) }) : null;
    const parts = [required ? t('form.required') : t('form.optional'), ...rule, def].filter(Boolean).join(' · ');
    return (
      <>
        {field.help ? <span className="block">{text(field.help)}</span> : null}
        <span className="block">{parts}</span>
      </>
    );
  };
}

/** Options of a select: the manifest's, or the ones an admin action returned. */
export function choicesOf(raw: unknown): ModuleChoice[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((x): ModuleChoice[] => {
    if (typeof x === 'string') return [{ value: x, label: { vi: x, en: x } }];
    if (x && typeof x === 'object' && 'value' in x) {
      const value = String((x as { value: unknown }).value);
      const l = (x as { label?: unknown }).label;
      const label = l && typeof l === 'object' && 'vi' in l ? (l as ModuleChoice['label']) : { vi: String(l ?? value), en: String(l ?? value) };
      return [{ value, label }];
    }
    return [];
  });
}

/** One field that is not write-only. */
export function FieldInput({
  field,
  value,
  onChange,
  required,
  error,
  options,
  placeholder,
}: {
  field: ModuleField;
  value: FormValue;
  onChange: (v: FormValue) => void;
  required: boolean;
  error: FormErrorCode | null;
  options?: OptionsSource;
  /** What an empty value means (a plan's override: the server group's value). */
  placeholder?: string;
}) {
  const { t } = useTranslation('modules');
  const text = useText();
  const hintOf = useFieldHint();
  const id = useId();
  const [loaded, setLoaded] = useState<ModuleChoice[] | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const label = text(field.label);
  const errorLine = error ? <span className="block text-critical">{t(`form.errors.${error}`)}</span> : null;
  const hint = (
    <>
      {hintOf(field, required)}
      {errorLine}
    </>
  );
  const s = typeof value === 'string' ? value : '';
  const k = field.kind;
  switch (FIELD_CONTROLS[k.type]) {
    case 'switch':
      return <SwitchRow label={label} hint={<span className="block text-xs text-muted-foreground">{hint}</span>} checked={value === true} onChange={(v) => onChange(v)} />;
    case 'number':
      return (
        <Field label={label} hint={hint}>
          <Input type="number" inputMode="numeric" value={s} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-invalid={!!error} />
        </Field>
      );
    case 'time':
      return (
        <Field label={label} hint={hint}>
          <Input type="time" value={s} onChange={(e) => onChange(e.target.value)} aria-invalid={!!error} />
        </Field>
      );
    case 'url':
      return (
        <Field label={label} hint={hint}>
          <div className="flex gap-2">
            <Input value={s} onChange={(e) => onChange(e.target.value)} className="font-mono" autoComplete="off" spellCheck={false} placeholder={placeholder ?? 'https://'} aria-invalid={!!error} />
            {typeof field.default === 'string' && s.trim() !== field.default ? (
              <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => onChange(String(field.default))}>
                {t('form.useDefault')}
              </Button>
            ) : null}
          </div>
        </Field>
      );
    case 'select': {
      if (k.type !== 'select') return null;
      const choices = loaded ?? k.options;
      const load = async () => {
        if (!options || !k.options_from) return;
        setLoading(true);
        setLoadError(null);
        try {
          const r = await api.runModuleAction(options.branch, options.module, options.instanceKey, k.options_from, { input: {} });
          if (r.outcome === 'ok') setLoaded(choicesOf(r.value));
          else setLoadError(new Error(r.refusal?.message ?? r.failure?.detail ?? r.outcome));
        } catch (e) {
          setLoadError(e);
        } finally {
          setLoading(false);
        }
      };
      return (
        <Field label={label} hint={hint}>
          {choices.length ? (
            <Select value={s || undefined} onValueChange={(v) => onChange(v)}>
              <SelectTrigger className="w-full" aria-invalid={!!error}>
                <SelectValue placeholder={placeholder ?? t('form.choose')} />
              </SelectTrigger>
              <SelectContent>
                {choices.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {text(o.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input value={s} onChange={(e) => onChange(e.target.value)} autoComplete="off" spellCheck={false} aria-invalid={!!error} />
          )}
          {k.options_from && !loaded ? (
            options?.active ? (
              <Button type="button" variant="outline" size="sm" className="mt-1.5 w-fit" onClick={() => void load()} disabled={loading}>
                <Download /> {t('form.loadOptions')}
              </Button>
            ) : (
              <span className="mt-1 block text-xs text-muted-foreground">{t('form.optionsUnavailable')}</span>
            )
          ) : null}
          {loadError ? <ModuleError error={loadError} /> : null}
        </Field>
      );
    }
    case 'checkboxes': {
      if (k.type !== 'multi_select') return null;
      const list = Array.isArray(value) ? value : [];
      return (
        <Field label={label} hint={hint} group>
          <ul className="grid gap-1.5">
            {k.options.map((o) => (
              <li key={o.value} className="flex items-center gap-2 text-sm">
                <Checkbox
                  id={`${id}-${o.value}`}
                  checked={list.includes(o.value)}
                  onCheckedChange={(c) => onChange(c === true ? [...list.filter((x) => x !== o.value), o.value] : list.filter((x) => x !== o.value))}
                />
                <label htmlFor={`${id}-${o.value}`}>{text(o.label)}</label>
              </li>
            ))}
          </ul>
        </Field>
      );
    }
    case 'input':
      return (
        <Field label={label} hint={hint}>
          <Input
            type={k.type === 'email' ? 'email' : k.type === 'phone' ? 'tel' : 'text'}
            inputMode={k.type === 'phone' ? 'tel' : undefined}
            value={s}
            onChange={(e) => onChange(e.target.value)}
            className={k.type === 'path' ? 'font-mono' : undefined}
            maxLength={k.type === 'text' ? k.max_len : undefined}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={!!error}
          />
        </Field>
      );
    case 'secret':
    case 'certificate':
    case 'upload':
      // Write-only kinds are drawn by `SecretInputField`.
      return null;
  }
}

/** A write-only field: a secret, a certificate or a file. */
export function SecretInputField({
  field,
  draft,
  onChange,
  stored,
  required,
  env,
}: {
  field: ModuleField;
  draft: SecretDraft;
  onChange: (d: SecretDraft) => void;
  stored: boolean;
  required: boolean;
  env: IntegrationEnvironment;
}) {
  const { t } = useTranslation('modules');
  const text = useText();
  const id = useId();
  const [file, setFile] = useState<{ name: string; size: number } | null>(null);
  const [tooBig, setTooBig] = useState(false);
  const k = field.kind;
  const missing = required && !stored && !secretDraftValid(draft);
  const hint = (
    <>
      {field.help ? <span className="block">{text(field.help)}</span> : null}
      {k.type === 'certificate' ? <span className="block">{k.private_key ? t('form.certificateWithKey') : t('form.certificateHint')}</span> : null}
      <span className="block">{required ? (stored ? t('form.required') : t('form.requiredWhenOn')) : t('form.optional')}</span>
      {missing ? <span className="block text-critical">{t('form.errors.REQUIRED')}</span> : null}
    </>
  );
  if (k.type !== 'file') {
    return <SecretField label={text(field.label)} draft={draft} onChange={onChange} canKeep={stored} keepLabel={t('form.keep')} env={env} multiline={k.type === 'certificate'} hint={hint} />;
  }
  const read = (f: File | undefined) => {
    if (!f) return;
    setTooBig(f.size > k.max_bytes);
    if (f.size > k.max_bytes) return;
    const done = (value: string) => {
      setFile({ name: f.name, size: f.size });
      onChange({ mode: 'VALUE', value, reference: '' });
    };
    if (isTextFile(k.mime, f)) void f.text().then(done);
    else
      void f.arrayBuffer().then((buf) => {
        let bin = '';
        for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
        done(btoa(bin));
      });
  };
  return (
    <fieldset className="grid gap-2 rounded-lg border p-3">
      <legend className="px-1 text-sm font-medium">{text(field.label)}</legend>
      {stored ? (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={draft.mode === 'KEEP'}
            onCheckedChange={(c) => {
              setFile(null);
              onChange(c === true ? { mode: 'KEEP', value: '', reference: '' } : { mode: 'VALUE', value: '', reference: '' });
            }}
          />
          {t('form.keep')}
        </label>
      ) : null}
      {draft.mode !== 'KEEP' ? (
        <div className="grid gap-1.5">
          <Input id={`${id}-file`} type="file" accept={k.mime.join(',')} onChange={(e) => read(e.target.files?.[0])} aria-label={text(field.label)} />
          {file ? <p className="text-xs text-success">{t('form.fileRead', { name: file.name, size: formatBytes(file.size) })}</p> : null}
          {tooBig ? <p className="text-xs text-critical">{t('form.fileTooBig', { size: formatBytes(k.max_bytes) })}</p> : null}
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">{t('form.fileHint', { types: k.mime.join(', '), size: formatBytes(k.max_bytes) })}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </fieldset>
  );
}

/**
 * Saves a draft of an instance's settings, starting from `base` (the draft, else the version in
 * force). `onDone` runs after the draft is saved; the result stays shown until the dialog closes.
 */
export function SchemaForm({
  manifest,
  fields,
  instance,
  base,
  env,
  canManageSecrets,
  onClose,
  onDone,
}: {
  manifest: ModuleManifest;
  fields: readonly ModuleField[];
  instance: ModuleInstance;
  base: ModuleVersion | null;
  env: IntegrationEnvironment;
  canManageSecrets: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('modules');
  const plain = fields.filter((f) => !isWriteOnly(f.kind));
  const secretFields = fields.filter((f) => isWriteOnly(f.kind));
  const [enabled, setEnabled] = useState(base?.enabled ?? true);
  const [values, setValues] = useState<Record<string, FormValue>>(() => Object.fromEntries(plain.map((f) => [f.key, initialValue(f, base)])));
  const [secrets, setSecrets] = useState<Record<string, SecretDraft>>(() => Object.fromEntries(secretFields.map((f) => [f.key, emptySecret(storedSecret(base, f.key))])));
  const [displayName, setDisplayName] = useState(instance.display_name ?? '');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const write = useWrite((body: SaveModuleDraftBody, opts) => api.saveModuleDraft(manifest.branch, manifest.id, instance.key, body, opts));
  const production = env === 'PRODUCTION';
  const options: OptionsSource = { branch: manifest.branch, module: manifest.id, instanceKey: instance.key, active: instance.enabled };
  // The branch core's own settings are always in use and have no name of their own.
  const isCore = manifest.id === CORE_MODULE;

  const required = (f: ModuleField) => isRequired(f, enabled, values, fields);
  const errorOf = (f: ModuleField): FormErrorCode | null => {
    const v = values[f.key] ?? '';
    if (isBlank(v)) return required(f) && f.default === null ? 'REQUIRED' : null;
    return checkValue(f, v, production);
  };
  // A secret left empty with nothing stored is sent as KEEP (= still not set): fine while it is not required.
  const leftEmpty = (f: ModuleField) => {
    const d = secrets[f.key];
    return !storedSecret(base, f.key) && (!d || (d.mode === 'VALUE' && !d.value));
  };
  const secretOk = (f: ModuleField) => {
    const d = secrets[f.key] ?? emptySecret(false);
    if (leftEmpty(f)) return !required(f);
    return secretDraftValid(d);
  };
  const secretTouched = secretFields.some((f) => secrets[f.key]?.mode !== 'KEEP' && !leftEmpty(f));
  const valid = plain.every((f) => !errorOf(f)) && secretFields.every(secretOk) && reason.trim() !== '' && confirmed && (!secretTouched || canManageSecrets);

  const submit = async () => {
    await write.run({
      enabled: enabled || isCore,
      values: Object.fromEntries(plain.map((f) => [f.key, payloadValue(f, values[f.key] ?? '')])),
      secrets: Object.fromEntries(secretFields.map((f) => [f.key, leftEmpty(f) ? { source: 'KEEP' as const } : secretInput(secrets[f.key] ?? emptySecret(false))])),
      base_version_id: base?.id ?? null,
      display_name: isCore ? null : displayName.trim() || null,
      reason: reason.trim(),
    });
  };

  if (write.state.phase === 'done') {
    const r = write.state.result;
    return (
      <div className="space-y-4" role="status">
        <Callout tone="ok" title={t('form.saved', { version: r.item.version })}>
          {manifest.test.kind === 'no_test' ? t('form.nextStepsNoTest') : t('form.nextSteps')}
        </Callout>
        <ModuleWarnings warnings={r.warnings} fields={fields} />
        <DialogFooter>
          <Button
            onClick={() => {
              onDone();
              onClose();
            }}
          >
            {t('form.close')}
          </Button>
        </DialogFooter>
      </div>
    );
  }

  const groups = groupFields(fields);
  const renderField = (f: ModuleField) =>
    isWriteOnly(f.kind) ? (
      <div key={f.key} className="sm:col-span-2">
        <SecretInputField
          field={f}
          draft={secrets[f.key] ?? emptySecret(false)}
          onChange={(d) => setSecrets((x) => ({ ...x, [f.key]: d }))}
          stored={storedSecret(base, f.key)}
          required={required(f)}
          env={env}
        />
      </div>
    ) : (
      <div key={f.key} className={f.kind.type === 'boolean' || f.kind.type === 'multi_select' ? 'sm:col-span-2' : undefined}>
        <FieldInput field={f} value={values[f.key] ?? ''} onChange={(v) => setValues((x) => ({ ...x, [f.key]: v }))} required={required(f)} error={errorOf(f)} options={options} />
      </div>
    );
  return (
    <div className="space-y-4" data-module-form={`${manifest.branch}/${manifest.id}`}>
      <EnvironmentBanner env={env} />
      {!manifest.production_ready && production ? <Callout tone="warn">{t('card.notProductionReady')}</Callout> : null}
      {isCore ? null : (
        <>
          <SwitchRow label={t('form.enabled')} hint={<span className="block text-xs text-muted-foreground">{t('form.enabledHint')}</span>} checked={enabled} onChange={setEnabled} />
          <Field label={t('form.displayName')} hint={t('form.displayNameHint')}>
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={100} />
          </Field>
        </>
      )}
      {groups.map((g, i) =>
        g.advanced ? (
          <details key={`adv-${i}`} className="rounded-lg border p-3 text-sm">
            <summary className="cursor-pointer font-medium">{t('config.advanced')}</summary>
            <div className="mt-3 grid items-start gap-3 sm:grid-cols-2">{g.fields.map(renderField)}</div>
          </details>
        ) : (
          <div key={`g-${i}`} className="grid items-start gap-3 sm:grid-cols-2">
            {g.fields.map(renderField)}
          </div>
        ),
      )}
      {secretTouched ? <p className={canManageSecrets ? 'text-xs text-muted-foreground' : 'text-xs text-critical'}>{t('form.secretPermission')}</p> : null}
      <ReasonField value={reason} onChange={setReason} />
      <ConfirmCheck checked={confirmed} onChange={setConfirmed} env={env} />
      {write.state.phase === 'error' ? <ModuleError error={write.state.error} fields={fields} /> : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('form.cancel')}
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={!valid || write.busy}>
          <Save /> {t('form.saveDraft')}
        </Button>
      </DialogFooter>
    </div>
  );
}
